import { Component, OnInit } from '@angular/core';
import { DashboardService } from '../services/dashboard.service';

import 'jspdf-autotable'; // Keep this import if needed by pdfMake internally
import * as moment from 'moment'; // Use moment for date formatting
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// Assuming xlsx is available for Excel export
import * as XLSX from 'xlsx';

// Define AMC interface
interface AMC {
  id: number;
  customerName: string;
  amcPeriod: number;
  expiryDate: string;
  renewalDate: string;
  isActive: boolean;
}

interface AMCNotice {
  type: 'expired' | 'warning' | 'info';
  message: string;
  deviceId: string;
  expiryDate: Date;
}

@Component({
  selector: 'app-amc-setting',
  templateUrl: './amc-setting.component.html',
  styleUrls: ['./amc-setting.component.scss']
})
export class AmcSettingComponent implements OnInit {
 amcList: any[] = [];
  filteredAmcList: any[] = [];
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 5; // Reduced from 10 to 5
  totalPages = 1;
  
  // Filters
  statusFilter: 'all' | 'expired' | 'warning' | 'active' = 'all';
  searchTerm = '';

  // Modal
  showModal = false;
  editingAMC: any = null;

  // Toast
  showToast = false;
  toastMessage = '';
  toastType: 'success' | 'warning' | 'error' = 'success';
  toastTimeout: any;

  // Download Dropdown
  showDownloadDropdown = false;
  deviceList: any[];

  constructor(private amcService: DashboardService) { }

  ngOnInit() {
    this.loadData();
    this.getIds()
  }

  loadData() {
    this.amcService.getamcreport().subscribe((res: any[]) => {
      console.log("API Response:", res);
      const filtered = res.filter(amc => {
        // Include all records, even those with invalid dates
        return true;
      }).map(amc => ({
        ...amc,
        customer: (amc.customer === null || amc.customer === undefined || amc.customer === '' || (typeof amc.customer === 'string' && amc.customer.toLowerCase() === 'null')) ? 'No name provided' : amc.customer
      }));

      // Sort by nearest expiry date (ascending)
      filtered.sort((a, b) => {
        const expiryA = this.getExpiryDate(a);
        const expiryB = this.getExpiryDate(b);

        const timeA = expiryA ? expiryA.getTime() : Infinity;
        const timeB = expiryB ? expiryB.getTime() : Infinity;

        return timeA - timeB;
      });

      this.amcList = filtered;
      console.log("Processed AMC List:", this.amcList);
      this.applyFilters();
    });
  }

  // Stats methods
  getExpiredCount(): number {
    return this.amcList.filter(amc => this.getAMCStatus(amc) === 'expired').length;
  }

  getExpiringSoonCount(): number {
    return this.amcList.filter(amc => this.getAMCStatus(amc) === 'warning').length;
  }

  getActiveCount(): number {
    return this.amcList.filter(amc => this.getAMCStatus(amc) === 'active').length;
  }

  filterByStatus(status: 'all' | 'expired' | 'warning' | 'active') {
    this.statusFilter = status;
    this.applyFilters();
  }
  getIds(){
    this.amcService.getamcids().subscribe((res: any[]) => {
     console.log("API Response:all ids", res)
     this.deviceList = res;
        });
 }
applyFilters() {
  let filtered = Array.from(this.amcList);
  console.log("filtered list", filtered);

  // Status filter
  if (this.statusFilter !== 'all') {
    filtered = filtered.filter(amc => {
      const status = this.getAMCStatus(amc);
      return status === this.statusFilter;
    });
  }

  // Search filter
  if (this.searchTerm) {
    const search = this.searchTerm.toLowerCase();
    filtered = filtered.filter(amc => 
      (amc.device_id && amc.device_id.toLowerCase().includes(search)) ||
      (amc.transformer_id && amc.transformer_id.toLowerCase().includes(search)) ||
      (amc.customer && amc.customer.toLowerCase().includes(search))   // ✅ added customer name
    );
  }

  this.filteredAmcList = filtered;
  this.totalPages = Math.ceil(this.filteredAmcList.length / this.itemsPerPage);
  this.currentPage = 1;
}

onDeviceChange() {
  const selectedDevice = this.deviceList.find(d => d.deviceId === this.editingAMC.device_id);
  if (selectedDevice) {
    this.editingAMC.transformer_id = selectedDevice.serialnumber;
  } else {
    this.editingAMC.transformer_id = '';
  }
}
  getAMCStatus(amc: any): 'expired' | 'warning' | 'active' {
    const expiryDate = this.getExpiryDate(amc);
    if (!expiryDate) return 'expired'; // Treat invalid dates as expired

    const now = new Date();
    const twoMonthsFromNow = new Date();
    twoMonthsFromNow.setMonth(now.getMonth() + 2);

    if (expiryDate < now) return 'expired';
    if (expiryDate <= twoMonthsFromNow) return 'warning';
    return 'active';
  }

  getExpiryDate(amc: any): Date | null {
    if (!amc.renewal_date || amc.renewal_date === "0000-00-00") {
      return null;
    }

    try {
      const renewal = new Date(amc.renewal_date);
      if (isNaN(renewal.getTime())) {
        return null;
      }

      const period = Number(amc.amc_period);
      // If period is 0, return the renewal date as expiry date
      if (period === 0) {
        return renewal;
      }

      const expiry = new Date(renewal);
      expiry.setFullYear(expiry.getFullYear() + period);
      return expiry;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  }

  formatDate(dateString: string | Date | null): string {
    if (!dateString) {
      return "Not Set";
    }
     if (typeof dateString === 'string' && dateString === "0000-00-00") {
      return "Not Set";
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
       // Use moment for consistent formatting if available and preferred
       return moment(date).format('MMM D, YYYY');

      // return date.toLocaleDateString('en-US', {
      //   year: 'numeric',
      //   month: 'short',
      //   day: 'numeric'
      // });
    } catch (error) {
      return "Invalid Date";
    }
  }

  getDaysUntilExpiry(amc: any): string {
    const expiryDate = this.getExpiryDate(amc);
    if (!expiryDate) return "N/A";

    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `Expired ${Math.abs(diffDays)} days ago`;
    } else if (diffDays === 0) {
      return "Expires today";
    } else {
      return `Expires in ${diffDays} days`;
    }
  }

  // Pagination methods
  get paginatedItems() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredAmcList.slice(start, start + this.itemsPerPage);
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  get pagesToDisplay(): number[] {
    const pages: number[] = [];
    const startPage = Math.max(1, this.currentPage - 1);
    const endPage = Math.min(this.totalPages, this.currentPage + 2); // Show current, prev, and next two pages

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Download Dropdown methods
  toggleDownloadDropdown() {
    this.showDownloadDropdown = !this.showDownloadDropdown;
  }

  downloadReport(format: 'excel' | 'pdf') {
    console.log(`Download report requested in ${format} format.`);
    
    // Map the filtered data to a format suitable for reports
    const dataToReport = this.filteredAmcList.map(item => ({
      'Device ID': item.device_id || '',
      'Transformer ID': item.transformer_id || '',
      'Customer': item.customer === 'No name provided' ? 'No name provided' : (item.customer || 'No name provided'),
      'Renewal Date': this.formatDate(item.renewal_date),
      'AMC Period (Years)': item.amc_period !== null && item.amc_period !== undefined ? item.amc_period : '',
      'Status': this.getAMCStatus(item).replace(/^./, (str: string) => str.toUpperCase()),
      'Expiry Date': this.formatDate(this.getExpiryDate(item)),
      'Expiry Info': this.getDaysUntilExpiry(item),
      'Dashboard Access': item.dashboard_access === 1 ? 'Yes' : 'No' 
    }));

    const reportFileName = `amc_report_${moment().format('YYYYMMDD_HHmmss')}`; // Generate dynamic file name

    if (format === 'excel') {
      this.showToastMessage('Generating Excel report...', 'warning');
      
      // Create a new workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]); // Create an empty worksheet

      // Add title
      const reportTitle = 'AMC Status Report';
      XLSX.utils.sheet_add_aoa(ws, [[reportTitle]], { origin: 'A1' });

      // Define headers from dataToReport keys
      const headers = Object.keys(dataToReport[0]);

      // Add headers row (starting from row 3, A3) - Leave row 2 blank for spacing
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A3' });

      // Add data rows (starting from row 4, A4)
      // XLSX.utils.sheet_add_json expects an array of objects
      XLSX.utils.sheet_add_json(ws, dataToReport, { origin: 'A4', skipHeader: true });

      // Optional: Merge cells for the title for better appearance
      if (headers.length > 0) {
         // Get the column letter for the last header
         const lastHeaderCol = XLSX.utils.encode_col(headers.length - 1);
         const mergeRange = `A1:${lastHeaderCol}1`;
         if (!ws['!merges']) ws['!merges'] = [];
         ws['!merges'].push(XLSX.utils.decode_range(mergeRange));

         // Optional: Basic styling for the title cell (bold)
         const titleCell = ws['A1'];
         if (titleCell) {
            titleCell.s = { // 's' is the style property
              font: { bold: true, sz: 14 }
              // alignment: { horizontal: 'center' } // Alignment is tricky without full style support or iterating cells
            };
         }

         // Optional: Basic styling for header row (bold)
          const headerRowRange = XLSX.utils.encode_range({r: 2, c: 0}, {r: 2, c: headers.length - 1});
          const headerCells = XLSX.utils.decode_range(headerRowRange);

          for (let C = headerCells.s.c; C <= headerCells.e.c; ++C) {
            const cell_address = {r: headerCells.s.r, c: C};
            const cell_ref = XLSX.utils.encode_cell(cell_address);
            const headerCell = ws[cell_ref];
            if (headerCell) {
                headerCell.s = { // 's' is the style property
                  font: { bold: true },
                   fill: { // Add fill for background color
                     fgColor: { rgb: "FFF2F2F2" } // Light grey color in ARGB format
                   }
                  // alignment: { horizontal: 'center' } // Again, alignment is more complex
                };
            }
          }

      }

      // Append the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'AMC Report');

      // Write and download the file
      XLSX.writeFile(wb, `${reportFileName}.xlsx`);

       setTimeout(() => {
         this.showToastMessage('Excel report download initiated.', 'success');
       }, 1000);

    } else if (format === 'pdf') {
      this.showToastMessage('Generating PDF report...', 'warning');

       const documentDefinition: any = {
        header: { text: 'AMC Status Report', style: 'reportHeader', alignment: 'center', margin: [0, 15, 0, 0] },
        content: [
          { text: '\n' },
          {
            table: {
              headerRows: 1,
              // Define widths for columns - Use * for proportional, auto for content
              widths: [ '*', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', '*' ],
              body: [
                // Table Header - explicitly define with styles
                [
                  { text: 'Device ID', style: 'tableHeader' },
                  { text: 'Transformer ID', style: 'tableHeader' },
                  { text: 'Customer', style: 'tableHeader' },
                  { text: 'Renewal Date', style: 'tableHeader' },
                  { text: 'AMC Period (Years)', style: 'tableHeader' },
                  { text: 'Status', style: 'tableHeader' },
                  { text: 'Expiry Date', style: 'tableHeader' },
                  { text: 'Expiry Info', style: 'tableHeader' },
                  { text: 'Dashboard Access', style: 'tableHeader' }
                ]
                // Table Body - Map data to array of arrays and concatenate
              ].concat(dataToReport.map(row => [
                 row['Device ID'],
                 row['Transformer ID'],
                 row['Customer'],
                 row['Renewal Date'],
                 row['AMC Period (Years)'],
                 row['Status'],
                 row['Expiry Date'],
                 row['Expiry Info'],
                 row['Dashboard Access']
              ]))
            },
             layout: { // Customize table layout for professional look
                hLineWidth: function(i: number, node: any): number { return (i === 0 || i === node.table.body.length) ? 1 : 1; }, // Horizontal lines
                vLineWidth: function(i: number, node: any): number { return 1; }, // Vertical lines
                hLineColor: function(i: number, node: any): string { return (i === 0 || i === node.table.body.length) ? '#999' : '#eee'; }, // Horizontal line color
                vLineColor: function(i: number, node: any): string { return '#eee'; }, // Vertical line color
                paddingLeft: function(i: number, node: any): number { return 8; },
                paddingRight: function(i: number, node: any): number { return 8; },
                paddingTop: function(i: number, node: any): number { return 5; },
                paddingBottom: function(i: number, node: any): number { return 5; }
            }
          }
        ],
        styles: {
           reportHeader: {
            fontSize: 16,
            bold: true,
            margin: [0, 10, 0, 10]
          },
          tableHeader: {
            bold: true,
            fontSize: 10,
            fillColor: '#f2f2f2',
            alignment: 'center',
          },
        },
        pageMargins: [ 40, 60, 40, 60 ]
      };

      pdfMake.createPdf(documentDefinition).download(`${reportFileName}.pdf`);

       setTimeout(() => {
         this.showToastMessage('PDF report download initiated.', 'success');
       }, 1000);
    }

    // Close the dropdown after selection
    this.showDownloadDropdown = false;
  }

  // Modal methods
  showAddAMCModal() {
    this.editingAMC = {
      device_id: '',
      transformer_id: '',
      customer: '',
      renewal_date: new Date().toISOString().split('T')[0],
      amc_period: 1,
      dashboard_access: 0
    };
    this.showModal = true;
  }

  showEditModal(amc: any) {
    // Create a copy of the AMC and ensure dashboard_access is a number\
    console.log("asdsadsasd",amc)
    if (amc.renewal_date) {
      const d = new Date(amc.renewal_date);
      amc.renewal_date = d.toISOString().substring(0, 10); 
      // gives "2022-03-21"
    }
    this.editingAMC = {
      ...amc,
      dashboard_access: amc.dashboard_access === 1 ? 1 : 0
    };
    this.showModal = true;
    console.log("asdsad",this.editingAMC)
  }

  closeModal() {
    this.showModal = false;
    this.editingAMC = null;
  }

  saveAMC() {
    if (!this.validateAMCData()) {
      return;
    }

    const amcData = {
      ...this.editingAMC,
      dashboard_access: this.editingAMC.dashboard_access ? 1 : 0
    };

    const saveOperation = this.editingAMC.id 
      ? this.amcService.updateAMC(amcData)
      : this.amcService.addAMC(amcData);

    saveOperation.subscribe({
      next: (response) => {
        this.showToastMessage(
          `AMC ${this.editingAMC.id ? 'updated' : 'added'} successfully`,
          'success'
        );
        this.closeModal();
        this.loadData(); // Reload the data
      },
      error: (error) => {
        console.error('Error saving AMC:', error);
        this.showToastMessage(
          `Failed to ${this.editingAMC.id ? 'update' : 'add'} AMC`,
          'error'
        );
      }
    });
  }

  private validateAMCData(): boolean {
    if (!this.editingAMC.device_id || this.editingAMC.device_id.trim() === '') {
      this.showToastMessage('Device ID is required', 'error');
      return false;
    }
    if (!this.editingAMC.transformer_id || this.editingAMC.transformer_id.trim() === '') {
      this.showToastMessage('Transformer ID is required', 'error');
      return false;
    }
    if (!this.editingAMC.customer || this.editingAMC.customer.trim() === '') {
      this.showToastMessage('Customer is required', 'error');
      return false;
    }
    if (!this.editingAMC.renewal_date) {
      this.showToastMessage('Renewal date is required', 'error');
      return false;
    }
    if (this.editingAMC.amc_period < 0) {
      this.showToastMessage('AMC period cannot be negative', 'error');
      return false;
    }
    return true;
  }

  // Toast methods
  showToastMessage(message: string, type: 'success' | 'warning' | 'error' = 'success') {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.toastTimeout = setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }

  // Input change handlers
  onRenewalDateChange(amc: any) {
    // You can add validation or additional logic here
    console.log('Renewal date changed:', amc);
  }

  onAMCPeriodChange(amc: any) {
    // You can add validation or additional logic here
    console.log('AMC period changed:', amc);
  }

  // Add a helper method to format customer name
  getCustomerName(customer: any): string {
    if (!customer) return 'Name not provided';
    if (typeof customer === 'string' && customer.trim() === '') return 'Name not provided';
    return customer;
  }

  // Add method to format customer name
  formatCustomerName(customer: any): string {
    if (customer === null || customer === undefined || customer === '') {
      return 'No name provided';
    }
    return customer;
  }
}
